import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

const DEFAULT_DESCRIPTION = '河马AV 影视内容详情页，为您介绍影视作品的基本信息与剧情简介。';

export default function DetailPage() {
  const { id = '' } = useParams();
  const movie = movies.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  usePageMeta({
    title: movie ? `${movie.title} - 详细介绍 - 河马AV` : '影视详情 - 河马AV',
    description: movie ? `${movie.title}（${movie.year}）${movie.genres.join('、')}，${movie.summary.slice(0, 70)}…` : DEFAULT_DESCRIPTION,
    keywords: movie ? `${movie.title},${movie.genres.join(',')},${movie.director},河马AV` : '河马AV,影视详情',
  });

  const related = useMemo(() => {
    if (!movie) return [];
    const sameCategory = movies.filter(
      (item) => item.id !== movie.id && item.categoryId === movie.categoryId,
    );
    const sameDirector = movies.filter(
      (item) => item.id !== movie.id && item.director === movie.director && item.categoryId !== movie.categoryId,
    );
    return [...sameCategory, ...sameDirector].slice(0, 5);
  }, [movie]);

  function showToast(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  }

  function handleFavorite() {
    if (!movie) return;
    const active = isFavorite(movie.id);
    toggleFavorite(movie.id);
    showToast(active ? '已取消收藏' : '收藏成功（前端演示）');
  }

  async function handleShare() {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  }

  if (!movie) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-600">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
          <p className="mt-3 text-sm text-foreground-500">该作品可能已下线或链接有误，您可以返回首页继续浏览。</p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const favorited = isFavorite(movie.id);

  const infoRows = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.cast.join(' / ') },
    { label: '制片国家/地区', value: movie.region },
    { label: '上映年份', value: String(movie.year) },
    { label: '类型', value: movie.genres.join(' / ') },
    { label: '片长', value: movie.duration },
  ];

  return (
    <Layout>
      <section className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb
            items={[
              { label: movie.categoryName, to: `/category/${movie.categoryId}` },
              { label: movie.title },
            ]}
          />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr] lg:gap-12">
          {/* Poster column */}
          <div className="mx-auto w-full max-w-[320px] lg:mx-0">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 高清海报`}
                title={`${movie.title} ${movie.categoryName}`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                  favorited
                    ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                    : 'border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-300 hover:text-accent-600'
                }`}
              >
                <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                {favorited ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                aria-label="复制分享链接"
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-lg text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-line" />
              </button>
            </div>
            <p className="mt-2 text-center text-xs text-foreground-400">收藏与分享为前端演示功能</p>
          </div>

          {/* Info column */}
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/category/${movie.categoryId}`}
                className="flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-1 text-xs text-primary-700 transition-colors hover:bg-primary-200"
              >
                <i className={movie.categoryName ? 'ri-price-tag-3-line' : ''} />
                {movie.categoryName}
              </Link>
              <span className="flex items-center gap-1 rounded-md bg-accent-100 px-3 py-1 font-label text-sm font-bold text-accent-700">
                <i className="ri-star-fill" />
                {movie.score.toFixed(1)}
              </span>
            </div>

            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              {movie.title}
            </h1>
            {movie.originalTitle && (
              <p className="mt-1.5 font-label text-sm text-foreground-400">{movie.originalTitle}</p>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              {movie.genres.map((genre) => (
                <span key={genre} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                  {genre}
                </span>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-24 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-[15px]">{movie.summary}</p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={`/category/${movie.categoryId}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-apps-2-line" />
                更多{movie.categoryName}
              </Link>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-14">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 animate-fade-up rounded-full bg-primary-950 px-5 py-2.5 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </Layout>
  );
}